// FILE: src/components/admin/AdminBits.jsx
// Small building blocks shared by the admin pages (plan WP-8): status chips
// (icon + words, never colour alone, MTO 5.4), the rating stars, a table
// wrapper that scrolls inside its card on phones, a titled group of figures
// and the in-place filter tabs kept in the URL.
import React from "react";
import { Box, Stack, Tab, Table, TableContainer, Tabs, Typography } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import MailOutlinedIcon from "@mui/icons-material/MailOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import TimerOffOutlinedIcon from "@mui/icons-material/TimerOffOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import DoNotDisturbOnOutlinedIcon from "@mui/icons-material/DoNotDisturbOnOutlined";
import PendingOutlinedIcon from "@mui/icons-material/PendingOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { StatusChip } from "../ui";
import { roleLabel, ticketStatusLabel, userStatusLabel } from "../../i18n/labels";
import { SLA_HOURS } from "./format";

/** Every status an account has, as chips: role, invited/enrolled, locked, untimed, ended. */
export function UserStatusChips({ user, now = Date.now() }) {
  if (!user) return null;
  const chips = [];
  if (user.role && user.role !== "student") {
    chips.push(<StatusChip key="role" tone="info" icon={<AdminPanelSettingsOutlinedIcon />} label={roleLabel(user.role)} />);
  }
  if (user.accountLocked) {
    chips.push(<StatusChip key="locked" tone="error" icon={<LockOutlinedIcon />} label={userStatusLabel("locked")} />);
  }
  if (user.invited) {
    chips.push(<StatusChip key="invited" tone="info" icon={<MailOutlinedIcon />} label={userStatusLabel("invited")} />);
  } else if (user.enrolled) {
    chips.push(<StatusChip key="enrolled" tone="success" icon={<CheckCircleOutlinedIcon />} label={userStatusLabel("enrolled")} />);
  } else if (user.role === "student") {
    chips.push(
      <StatusChip key="not-enrolled" icon={<DoNotDisturbOnOutlinedIcon />} label={userStatusLabel("not-enrolled")} />
    );
  }
  const expires = user.enrolmentExpiresAt ? new Date(user.enrolmentExpiresAt).getTime() : null;
  if (user.expiredAt || (expires && expires < now)) {
    chips.push(<StatusChip key="ended" icon={<EventBusyOutlinedIcon />} label={userStatusLabel("enrolment-ended")} />);
  }
  if (user.flags?.untimed || user.untimed) {
    chips.push(<StatusChip key="untimed" tone="warning" icon={<TimerOffOutlinedIcon />} label={userStatusLabel("untimed")} />);
  }
  return (
    <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.75 }}>
      {chips}
    </Stack>
  );
}

export function TicketStatusChip({ status }) {
  return status === "RESOLVED" ? (
    <StatusChip tone="success" icon={<TaskAltOutlinedIcon />} label={ticketStatusLabel(status)} />
  ) : (
    <StatusChip tone="info" icon={<PendingOutlinedIcon />} label={ticketStatusLabel(status)} />
  );
}

/** The 48-hour answer target has passed (MTO 10.2). */
export function OverdueChip() {
  return <StatusChip tone="warning" icon={<WarningAmberOutlinedIcon />} label={`Over ${SLA_HOURS} h`} />;
}

const COURSE_STATUS = {
  completed: { tone: "success", Icon: TaskAltOutlinedIcon },
  "in-progress": { tone: "info", Icon: HourglassEmptyOutlinedIcon },
  "not-started": { tone: "default", Icon: PendingOutlinedIcon }
};

/** "completed" / "in progress" / "PASSED" … as one chip. */
export function CourseStatusChip({ status }) {
  const key = String(status || "")
    .toLowerCase()
    .replace(/[\s_]+/g, "-")
    .replace(/^passed$/, "completed");
  const { tone, Icon } = COURSE_STATUS[key] || COURSE_STATUS["not-started"];
  return <StatusChip tone={tone} icon={<Icon />} label={userStatusLabel(key || "not-started")} />;
}

/** "4 out of 5" as words, with five stars that carry the same text. */
export function RatingStars({ value, max = 5, showText = true }) {
  const n = Math.max(0, Math.min(max, Math.round(Number(value) || 0)));
  const text = `${n} out of ${max}`;
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
      <Box role="img" aria-label={`${text} stars`} sx={{ display: "inline-flex", color: "warning.main" }}>
        {Array.from({ length: max }).map((_, i) =>
          i < n ? <StarIcon key={i} aria-hidden /> : <StarBorderIcon key={i} aria-hidden />
        )}
      </Box>
      {showText && (
        <Typography component="span" aria-hidden sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
          {text}
        </Typography>
      )}
    </Stack>
  );
}

/**
 * A table that scrolls sideways inside its card on narrow screens. The
 * scroll area is a labelled, focusable region so keyboard users can scroll
 * it too (WCAG 2.1.1).
 */
export function ResponsiveTable({ label, minWidth = 720, children, sx }) {
  return (
    <TableContainer
      role="region"
      aria-label={label}
      tabIndex={0}
      sx={{ overflowX: "auto", border: "1px solid", borderColor: "divider", borderRadius: 3, ...sx }}
    >
      <Table aria-label={label} sx={{ minWidth }}>
        {children}
      </Table>
    </TableContainer>
  );
}

/** A titled group of figures (an <h2> over a grid of StatCards). */
export function FigureGroup({ id, title, description, action, children, columns = 3 }) {
  const headingId = `${id}-title`;
  return (
    <Box component="section" aria-labelledby={headingId} sx={{ mb: 4 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        sx={{ alignItems: { xs: "flex-start", sm: "flex-end" }, justifyContent: "space-between", mb: 2 }}
      >
        <Box>
          <Typography id={headingId} variant="h3" component="h2">
            {title}
          </Typography>
          {description && <Typography sx={{ color: "text.secondary", mt: 0.5 }}>{description}</Typography>}
        </Box>
        {action}
      </Stack>
      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: `repeat(${columns}, minmax(0, 1fr))` }
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

/**
 * Tabs that filter a list in place (arrow keys move between them). Pair
 * with an element that has id `${idPrefix}-panel`, role="tabpanel" and
 * aria-labelledby={`${idPrefix}-tab-${value}`}.
 */
export function FilterTabs({ label, value, onChange, options, idPrefix }) {
  return (
    <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
      <Tabs
        value={value}
        onChange={(_, v) => onChange(v)}
        aria-label={label}
        variant="scrollable"
        scrollButtons={false}
      >
        {options.map((o) => (
          <Tab
            key={o.value}
            value={o.value}
            id={`${idPrefix}-tab-${o.value}`}
            aria-controls={`${idPrefix}-panel`}
            icon={o.icon || undefined}
            iconPosition="start"
            label={o.count === undefined ? o.label : `${o.label} (${o.count})`}
            sx={{ minHeight: 48 }}
          />
        ))}
      </Tabs>
    </Box>
  );
}
